import React from 'react';
import { View, Text } from 'react-native';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import Icon from '@shared/components/md-design-system/Icon/Icon';
import { colorPalette } from '@shared/config/colorsConfig';
import { Button } from '@shared/components/md-design-system/Button/Button';
import { getDoctorPronoun, getDoctorTitle } from '@shared/utils/doctor';
import { getFormattedTimestamp } from '@shared/utils/date';
import { APPOINTMENT_MODALITY, APPOINTMENT_CARD_STATUS, TAppointmentModality, TAppointmentCardStatus } from '../../constants/appointmentModality';

export type AppointmentType = TAppointmentModality;
export type AppointmentCardStatus = TAppointmentCardStatus;

export interface AppointmentCardProps {
    type: AppointmentType;
    status: AppointmentCardStatus;
    time?: string;
    date?: string;
    activityName?: string;
    professional?: {
        fullName?: string;
        title?: string;
    };
    reason?: string;
    onConnect?: () => void;
}

const formatDateOnly = (dateStr: string): string => {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${String(year).slice(-2)}`;
};

export const AppointmentCard: React.FC<AppointmentCardProps> = ({
    type,
    status,
    time,
    date,
    activityName,
    professional,
    reason,
    onConnect,
}) => {
    const { t } = dynamicTranslations();

    const formattedTime = time ? time.substring(0, 5) : '';
    const isUpcoming = status === APPOINTMENT_CARD_STATUS.UPCOMING;
    const isScheduled = status === APPOINTMENT_CARD_STATUS.SCHEDULED;
    const isCompleted = status === APPOINTMENT_CARD_STATUS.COMPLETED;
    const isMissed = status === APPOINTMENT_CARD_STATUS.MISSED;

    const title = (() => {
        if (isMissed) {
            if (type === APPOINTMENT_MODALITY.TELEVISITA) return t('televisits.notCompleted');
            const modality = t(`appointments.card.modality.${type}`);
            return t('appointments.card.notCompleted', { modality });
        }
        if (type === APPOINTMENT_MODALITY.TELEVISITA) {
            return isUpcoming
                ? t('televisits.scheduledAt', { time: formattedTime })
                : t('televisits.televisit');
        }
        if (type === APPOINTMENT_MODALITY.AT_HOME) {
            return t('appointments.card.modality.at_home');
        }
        if (type === APPOINTMENT_MODALITY.TELEASSISTANCE) {
            return isUpcoming
                ? t('appointments.card.scheduledAt', { modality: t('appointments.card.modality.teleassistance'), time: formattedTime })
                : t('appointments.card.modality.teleassistance');
        }
        return t('appointments.card.scheduledAt', {
            modality: t('appointments.card.modality.on_site'),
            time: formattedTime,
        });
    })();

    const description = (() => {
        if (type === APPOINTMENT_MODALITY.TELEVISITA) {
            const professionalTitle = getDoctorTitle(professional?.title, t);
            const professionalPronoun = getDoctorPronoun(professional?.title, t);
            const reasonLabel = reason
                ? t(`televisits.reasons.${reason}`, { defaultValue: reason })
                : t('televisits.televisit');
            return t('televisits.forReasonWithDoctor', {
                reason: reasonLabel,
                pronom: professionalPronoun,
                professional: [professionalTitle, professional?.fullName].filter(Boolean).join(' '),
            });
        }
        const activityLabel = activityName ?? '';
        const professionalTitle = getDoctorTitle(professional?.title, t);
        const professionalStr = professional
            ? [professionalTitle, professional.fullName].filter(Boolean).join(' ')
            : t('appointments.card.organization');
        return t('appointments.card.withDoctor', { activityName: activityLabel, professional: professionalStr });
    })();

    const dateDisplay = (() => {
        if (!date || isUpcoming) return null;
        if (type === APPOINTMENT_MODALITY.AT_HOME) return formatDateOnly(date);
        return time ? getFormattedTimestamp(`${date}T${time}`) : formatDateOnly(date);
    })();

    const icon = (() => {
        if (isCompleted) return (
            <View className="w-8 h-8 rounded-full bg-success-100 justify-center items-center">
                <Icon name="check" size={16} color={colorPalette.success[700]} />
            </View>
        );
        if (isScheduled) return (
            <View className="w-8 h-8 rounded-full bg-primary-100 justify-center items-center">
                <Icon name="calendar" size={16} color={colorPalette.primary[700]} />
            </View>
        );
        if (isMissed) return (
            <View className="w-8 h-8 rounded-full bg-gray-300 justify-center items-center">
                <Icon name="close" size={16} color={colorPalette.gray[500]} />
            </View>
        );
        return null;
    })();

    return (
        <View className={`${isMissed ? 'bg-gray-200' : 'bg-white border border-gray-300'} rounded-3xl p-4 mb-3`}>
            <View className="flex-col gap-3">
                <View className="flex-row items-center gap-3">
                    {icon}
                    <Text className={`font-semibold text-base flex-1 ${isMissed ? 'text-gray-500' : 'text-gray-900'}`}>
                        {title}
                    </Text>
                </View>
                <Text className={`text-base ${isMissed ? 'text-gray-500' : 'text-gray-700'}`}>
                    {description}
                </Text>
                {dateDisplay && (
                    <Text className={`text-sm ${isMissed ? 'text-gray-500' : 'text-gray-700'}`}>{dateDisplay}</Text>
                )}
                {(type === APPOINTMENT_MODALITY.TELEVISITA || type === APPOINTMENT_MODALITY.TELEASSISTANCE) && isUpcoming  && onConnect && (
                    <Button variant="tiffany" icon="video-camera" onPress={onConnect}>
                        <Text className="text-black font-semibold ml-2 text-base">
                            {t('televisits.connect')}
                        </Text>
                    </Button>
                )}
            </View>
        </View>
    );
};
